import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
//old import { Info, MessageSquare, Plus } from "lucide-react";
import { Info, MessageSquare, Paperclip, Plus } from "lucide-react";
import api from "../../api/api";
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";
//old import { Alert, EmptyState, Panel, Pill, apiError, formatDate, useOrgStyles } from "../../components/org/orgUi";
import {
    Alert, EmptyState, Panel, Pill, apiError, downloadChangeRequestAttachment, formatDate, useOrgStyles,
} from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";
import { CHANGE_STATUS, FEATURE_LABEL, RequestModal } from "./OrgScoring";

/* Requests (Evan, Oct 16): every change request the organization sent Pono
   Pilot, with its status and Pono Pilot's note, in one place, and the
   Request a change form (moved out of Checklists & Scoring, which links here). A
   link with ?feature=<part> opens the form with that part picked. Org admins only (adminOnly in ORG_PAGES),
   like the change-request routes. */

export default function OrgRequests() {
    const { orgId, goTo, isPlatformAdmin } = useOrgPortal();
    const s = useOrgStyles();
    const [searchParams, setSearchParams] = useSearchParams();
    const [requests, setRequests] = useState(null);
    const [requestsError, setRequestsError] = useState("");
    const [notice, setNotice] = useState("");
    const [asking, setAsking] = useState(null); // null = closed; otherwise the feature the request starts with ("" = none)
    // The rules and categories Checklists & Scoring shows, for the form's "Which part?"
    const [checklist, setChecklist] = useState(null);
    const [scoring, setScoring] = useState(null);

    const loadRequests = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/change-requests`);
            setRequests(res.data.requests || []);
            setRequestsError("");
        } catch (err) {
            setRequestsError(apiError(err, "Couldn't load your requests."));
        }
    }, [orgId]);

    useEffect(() => { loadRequests(); }, [loadRequests]);

    // If these fail, Which part? still offers Not sure and Add a new rule / category.
    useEffect(() => {
        //old api.get("/resume/checklist-rules")
            //old .then((res) => setChecklist({ name: res.data.name, rules: res.data.rules || [] }))
            //old .catch(() => {});
        api.get(`/org/${orgId}/scoring`)
            //old .then((res) => setScoring(res.data))
            // (with the Resume Checklist the org's students get, its own or the standard one)
            .then((res) => { setScoring(res.data); setChecklist(res.data.checklist || null); })
            .catch(() => {});
    }, [orgId]);

    // A link with ?feature=<part> opens the form for that part, then the parameter
    // is dropped so a refresh or Back doesn't open it again.
    useEffect(() => {
        const feature = searchParams.get("feature");
        if (feature === null) return;
        setNotice("");
        setAsking(FEATURE_LABEL[feature] ? feature : "");
        const rest = new URLSearchParams(searchParams);
        rest.delete("feature");
        setSearchParams(rest, { replace: true });
    }, [searchParams, setSearchParams]);

    const ask = () => {
        setNotice("");
        setAsking("");
    };
    const onSent = async () => {
        setAsking(null);
        setNotice("Request sent. Pono Pilot will update its status here.");
        await loadRequests();
    };

    return (
        <div className="space-y-6">
            <Alert kind="success" onClose={() => setNotice("")}>{notice}</Alert>
            <div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${s.subPanel}`}>
                <Info size={18} className={`mt-0.5 flex-shrink-0 ${s.muted}`} />
                <p className={s.body}>
                    Ask Pono Pilot to change your Resume Checklist or how Resume Analysis, Mock Interview
                    {/* old and Elevator Pitch score your students. Pono Pilot reviews each request and updates its */}
                    {" "}and Elevator Pitch score your clients. Pono Pilot reviews each request and updates its
                    {/* old status here. To see what your students get today, open{" "} */}
                    {" "}status here. To see what your clients get today, open{" "}
                    <button type="button" className={s.btnLink} onClick={() => goTo("scoring")}>Checklists & Scoring</button>.
                    {/* anything else the org asks Pono Pilot for goes here too (Evan, Oct 16: all of its requests in one place) */}
                    {" "}Anything else, like more licenses, a higher program or admin limit, your plan or your organization's
                    name, is a request too: pick Something else.
                    {/* a Pono Pilot admin answers the requests somewhere else */}
                    {isPlatformAdmin && <> You're viewing this as Pono Pilot: set each request's status in{" "}
                        {/* old <Link className={s.btnLink} to="/admin/organizations">Admin → Organizations → Change requests</Link>.</>} */}
                        {/* (it opens the Change requests tab itself) */}
                        <Link className={s.btnLink} to="/admin/organizations?tab=changes">Admin → Organizations → Change requests</Link>.</>}
                </p>
            </div>

            <Panel
                title={<span className="inline-flex items-center gap-2"><MessageSquare size={18} /> Your requests</span>}
                actions={<button type="button" className={s.btnPrimary} onClick={ask}><Plus size={14} /> New request</button>}
                padded={false}
            >
                {requestsError && (
                    <div className="px-5 pt-4">
                        <Alert onClose={requests ? () => setRequestsError("") : undefined}>{requestsError}</Alert>
                    </div>
                )}
                {requests === null ? (
                    !requestsError && <InlineTableSkeleton isDarkMode={s.dark} rows={3} cols={4} bordered={false} />
                ) : requests.length === 0 ? (
                    <EmptyState icon={MessageSquare} title="No requests yet"
                                //old body="Use New request to ask Pono Pilot to change a checklist rule or a scoring category." />
                                body="Use New request to ask Pono Pilot to change a checklist rule or a scoring category, or for more licenses, a higher limit, a new plan or a new name." />
                ) : (
                    <div className="relative overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                    <th className={s.th}>Sent</th>
                                    <th className={s.th}>About</th>
                                    <th className={s.th}>Request</th>
                                    <th className={s.th}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map((r) => (
                                    <tr key={r.ChangeRequestID} className={`border-b last:border-0 ${s.row}`}>
                                        <td className={`${s.td} whitespace-nowrap`}>{formatDate(r.CreatedOn)}</td>
                                        <td className={s.td}>
                                            <div className={`font-semibold ${s.text}`}>{FEATURE_LABEL[r.FeatureName] || r.FeatureName}</div>
                                            {r.TargetItem && <div className={`mt-1 max-w-xs break-words text-xs ${s.muted}`}>{r.TargetItem}</div>}
                                        </td>
                                        <td className={s.td}>
                                            <div className="min-w-[14rem] max-w-md whitespace-pre-wrap break-words">{r.Details}</div>
                                            {/* the PDF that came with it */}
                                            {r.AttachmentFileName && (
                                                <button type="button" className={`${s.btnLink} mt-1 inline-flex max-w-md items-start gap-1 text-left !text-xs`}
                                                        onClick={() => downloadChangeRequestAttachment(orgId, r.ChangeRequestID, r.AttachmentFileName)
                                                            .catch((err) => setRequestsError(apiError(err)))}>
                                                    <Paperclip size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                                                    <span className="[overflow-wrap:anywhere]">{r.AttachmentFileName}</span>
                                                </button>
                                            )}
                                        </td>
                                        <td className={s.td}>
                                            <Pill tone={(CHANGE_STATUS[r.Status] || {}).tone}>{(CHANGE_STATUS[r.Status] || {}).label || r.Status}</Pill>
                                            {r.ReviewNote && <div className={`mt-1 max-w-xs whitespace-pre-wrap break-words text-xs ${s.body}`}>{r.ReviewNote}</div>}
                                            {r.ReviewedOn && <div className={`mt-1 text-xs ${s.muted}`}>Updated {formatDate(r.ReviewedOn)}</div>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>

            {asking !== null && (
                <RequestModal orgId={orgId} featureName={asking} checklist={checklist} scoring={scoring}
                              onClose={() => setAsking(null)} onSent={onSent} />
            )}
        </div>
    );
}
